import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import BookPicker from "./BookPicker";
import { getBooks } from "../api";
import { track } from "../lib/analytics";
import { BookOpen, ChevronsRight } from "lucide-react";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const sortName = (name) => name.replace(/^[123]\s+/, "");

// Always-visible A–Z strip at the bottom. Drag the Bible knob across the
// letters (the one under your finger magnifies); release to see the books
// for that letter. Tapping the knob opens the full Book Picker.
export default function BibleSlider() {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/read\/([^/]+)\//);
  const currentBook = match ? match[1] : null;

  const [books, setBooks] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [forceBookId, setForceBookId] = useState(null);
  const [hot, setHot] = useState(null); // currently magnified letter
  const [popupLetter, setPopupLetter] = useState(null);
  const [asleep, setAsleep] = useState(false);

  const barRef = useRef(null);
  const letterRefs = useRef({});
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const sleepTimer = useRef(null);

  const onReader = !!currentBook;

  // on the reader, dim the strip after a few idle seconds so it stays out of
  // the way while reading; touching it wakes it back up.
  const scheduleSleep = () => {
    clearTimeout(sleepTimer.current);
    if (!onReader) return;
    sleepTimer.current = setTimeout(() => setAsleep(true), 4000);
  };
  const wake = () => {
    setAsleep(false);
    scheduleSleep();
  };
  useEffect(() => {
    if (onReader) {
      setAsleep(true); // fade out right away when you land on a chapter
    } else {
      setAsleep(false);
      clearTimeout(sleepTimer.current);
    }
    return () => clearTimeout(sleepTimer.current);
    // eslint-disable-next-line
  }, [onReader, pathname]);

  useEffect(() => {
    getBooks().then(setBooks).catch(() => {});
  }, []);

  const letterMap = useMemo(() => {
    const m = {};
    books.forEach((b) => {
      const c = sortName(b.name)[0]?.toUpperCase();
      if (!c) return;
      (m[c] ||= []).push(b);
    });
    return m;
  }, [books]);

  const letters = useMemo(
    () => ALPHABET.filter((ch) => letterMap[ch]?.length),
    [letterMap]
  );

  const letterAtX = (clientX) => {
    let best = null, bestDist = Infinity;
    for (const ch of letters) {
      const el = letterRefs.current[ch];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const d = Math.abs(cx - clientX);
      if (d < bestDist) { bestDist = d; best = ch; }
    }
    return best;
  };

  const startDrag = (e) => {
    wake();
    draggingRef.current = true;
    movedRef.current = false;
    try { barRef.current?.setPointerCapture?.(e.pointerId); } catch (_) {}
    document.body.classList.add("no-scroll");
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const ch = letterAtX(x);
    setHot(ch);
    if (ch && letterMap[ch]?.length) setPopupLetter(ch);
  };

  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current) return;
      movedRef.current = true;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const ch = letterAtX(x);
      setHot(ch);
      // show the book menu live as you reach a letter — no release needed
      if (ch && letterMap[ch]?.length) setPopupLetter(ch);
      if (e.cancelable) e.preventDefault();
    };
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.classList.remove("no-scroll");
      setHot(null); // stop magnifying; leave the book menu open to tap a book
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [letterMap]);

  const tapLetter = (ch) => {
    if (!letterMap[ch]?.length) return;
    if (movedRef.current) return; // drag already handled it
    setPopupLetter(ch);
  };

  const openPicker = () => {
    if (movedRef.current) return;
    track("bible_button_open");
    setForceBookId(null);
    setPickerOpen(true);
  };

  const chooseBook = (b) => {
    track("bible_slider_open_book", { book: b.id });
    setPopupLetter(null);
    setForceBookId(b.id);
    setPickerOpen(true);
  };

  const idx = (ch) => letters.indexOf(ch);
  const scaleFor = (ch) => {
    if (!hot) return 1;
    const d = Math.abs(idx(ch) - idx(hot));
    return d === 0 ? 2 : d === 1 ? 1.45 : d === 2 ? 1.18 : 1;
  };
  const liftFor = (ch) => {
    if (!hot) return 0;
    const d = Math.abs(idx(ch) - idx(hot));
    return d === 0 ? -26 : d === 1 ? -12 : d === 2 ? -4 : 0;
  };

  return (
    <>
      {pathname === "/download" || pathname.startsWith("/admin") ? null : (
      <>
      {/* books popup for a chosen letter */}
      {popupLetter && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setPopupLetter(null)}
            data-testid="slider-popup-backdrop"
          />
          <div className="fixed bottom-32 left-1/2 z-50 w-[min(92vw,30rem)] -translate-x-1/2">
            <div
              data-testid="slider-popup"
              className="font-ui max-h-[60vh] w-full overflow-y-auto overscroll-contain rounded-3xl border border-border bg-popover/95 p-5 shadow-2xl backdrop-blur-xl float-up"
            >
            <div className="mb-3 flex items-baseline gap-2">
              <span className="font-serif-display text-3xl text-primary">{popupLetter}</span>
              <span className="text-xs text-muted-foreground">
                {letterMap[popupLetter].length} book{letterMap[popupLetter].length > 1 ? "s" : ""}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {letterMap[popupLetter].map((b) => (
                <button
                  key={b.id}
                  data-testid={`slider-book-${b.id}`}
                  onClick={() => chooseBook(b)}
                  className="rounded-xl border border-border px-3 py-2.5 text-left text-sm transition-all hover:border-primary hover:bg-muted"
                >
                  <span className="block truncate font-medium">{b.name}</span>
                  <span className="text-xs text-muted-foreground">{b.chapters} ch</span>
                </button>
              ))}
            </div>
            </div>
          </div>
        </>
      )}

      {/* the A–Z strip — floating pill lifted off the bottom edge */}
      <div
        ref={barRef}
        data-testid="bible-slider"
        onPointerEnter={wake}
        onPointerDown={wake}
        onTouchStart={wake}
        className={`fixed inset-x-3 bottom-4 z-40 flex items-center gap-2 rounded-3xl border border-border bg-background/90 px-3 py-3 shadow-2xl backdrop-blur-xl transition-opacity duration-500 sm:inset-x-auto sm:left-1/2 sm:w-[40rem] sm:-translate-x-1/2 ${
          asleep ? "opacity-30" : "opacity-100"
        }`}
        style={{ touchAction: "none" }}
      >
        <button
          onPointerDown={startDrag}
          onTouchStart={startDrag}
          onClick={openPicker}
          data-testid="bible-fab"
          aria-label="Open the Bible"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-primary/20 transition-transform active:scale-95"
        >
          <BookOpen className="h-5 w-5" />
        </button>

        <div
          className="relative flex h-10 flex-1 select-none items-end justify-between"
          onPointerDown={startDrag}
          onTouchStart={startDrag}
        >
          {/* idle marketing label — sits over the letters, fades out on slide */}
          <div
            data-testid="slider-hint"
            className={`font-ui pointer-events-none absolute inset-0 z-10 flex items-center justify-center gap-1.5 rounded-xl bg-background/95 text-xs font-bold text-primary transition-opacity duration-300 ${
              hot ? "opacity-0" : "opacity-100"
            }`}
          >
            <span>Slide A–Z to jump to a book</span>
            <ChevronsRight className="h-3.5 w-3.5 slide-nudge" />
          </div>
          {letters.map((ch) => {
            return (
              <button
                key={ch}
                ref={(el) => (letterRefs.current[ch] = el)}
                onClick={() => tapLetter(ch)}
                data-testid={`slider-letter-${ch}`}
                className={`font-ui flex h-10 flex-1 items-end justify-center text-center text-[0.78rem] font-bold transition-transform duration-100 text-foreground ${
                  hot === ch ? "text-primary" : ""
                }`}
                style={{
                  transform: `translateY(${liftFor(ch)}px) scale(${scaleFor(ch)})`,
                  transformOrigin: "bottom center",
                }}
              >
                {ch}
              </button>
            );
          })}
        </div>
      </div>

      <BookPicker
        open={pickerOpen}
        onOpenChange={(o) => { setPickerOpen(o); if (!o) setForceBookId(null); }}
        currentBook={currentBook}
        forceBookId={forceBookId}
      />
      </>
      )}
    </>
  );
}
